import React, { useEffect, useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Mail, Plus, RefreshCw, Trash2, X, Upload, FileSpreadsheet, Paperclip, Search, ArrowDown, ArrowUp } from "lucide-react";
import GmailAppPasswordModal from "@/components/email/GmailAppPasswordModal";

export default function Email() {
  const [accounts, setAccounts] = useState([]);
  const [messages, setMessages] = useState([]);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({ q: "", account_id: "", direction: "", job_number: "", has_attachments: "", search_scope: "all", date_from: "", date_to: "" });
  const [showAdd, setShowAdd] = useState(false);
  const [selected, setSelected] = useState(null);
  const [syncingId, setSyncingId] = useState(null);
  const fileRef = useRef(null);

  const loadAccounts = async () => {
    const { data } = await api.get("/email/accounts");
    setAccounts(data);
  };
  const loadStats = async () => {
    const { data } = await api.get("/email/stats");
    setStats(data);
  };
  const loadMessages = async () => {
    const params = {};
    Object.entries(filters).forEach(([k, v]) => { if (v !== "" && v !== null) params[k] = v; });
    const { data } = await api.get("/email/messages", { params });
    setMessages(data);
  };
  useEffect(() => { loadAccounts(); loadStats(); loadMessages(); }, []);
  useEffect(() => { loadMessages(); /* eslint-disable-line */ }, [filters]);

  const sync = async (acc) => {
    setSyncingId(acc.id);
    try {
      const { data } = await api.post(`/email/accounts/${acc.id}/sync`, null, { params: { days: 60, folder: "ALL" } });
      toast.success(`Imported ${data.imported} new emails`);
      loadAccounts(); loadStats(); loadMessages();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Sync failed");
    }
    setSyncingId(null);
  };

  const removeAcc = async (id) => {
    if (!confirm("Remove this account and all its emails?")) return;
    await api.delete(`/email/accounts/${id}`);
    loadAccounts(); loadStats(); loadMessages();
  };

  const onCsvImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/email/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${data.imported} emails`);
      loadMessages(); loadStats();
    } catch (err) { toast.error(err.response?.data?.detail || "Import failed"); }
    e.target.value = "";
  };

  const downloadTemplate = async () => {
    const token = localStorage.getItem("session_token");
    const res = await api.get("/email/template", { params: token ? { auth: token } : {}, responseType: "blob" });
    const u = URL.createObjectURL(new Blob([res.data]));
    const a = document.createElement("a"); a.href = u; a.download = "emails_template.xlsx"; a.click();
    URL.revokeObjectURL(u);
  };

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="label-mono">COMMUNICATIONS</div>
          <h1 className="font-heading text-4xl font-black tracking-tight">Email Tracking</h1>
          <p className="text-sm text-zinc-600 mt-1 font-mono">Gmail IMAP · Multi-account · Auto-extract job numbers · Search attachments</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <input ref={fileRef} type="file" accept=".xlsx,.csv" onChange={onCsvImport} className="hidden" data-testid="email-csv-input" />
          <button onClick={downloadTemplate} className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-1" data-testid="email-template-btn">
            <FileSpreadsheet className="w-3.5 h-3.5" /> TEMPLATE
          </button>
          <button onClick={() => fileRef.current?.click()} className="brut-btn-secondary px-3 py-2 text-xs flex items-center gap-1" data-testid="email-csv-btn">
            <Upload className="w-3.5 h-3.5" /> CSV IMPORT
          </button>
          <button onClick={() => setShowAdd(true)} className="brut-btn px-4 py-2 text-sm flex items-center gap-2" data-testid="add-email-account-btn">
            <Plus className="w-4 h-4" /> ADD GMAIL
          </button>
        </div>
      </div>

      {/* Stats strip */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <Stat label="ACCOUNTS" value={stats.accounts ?? 0} />
        <Stat label="TOTAL" value={stats.total ?? 0} />
        <Stat label="RECEIVED" value={stats.received ?? 0} icon={ArrowDown} />
        <Stat label="SENT" value={stats.sent ?? 0} icon={ArrowUp} />
        <Stat label="WITH ATTACH." value={stats.with_attachments ?? 0} icon={Paperclip} />
      </div>

      {/* Accounts */}
      <div className="brut-card p-4">
        <div className="label-mono mb-3">CONNECTED ACCOUNTS</div>
        {accounts.length === 0 ? (
          <div className="text-center py-6 font-mono text-xs text-zinc-400">
            NO ACCOUNTS YET — Click "Add Gmail" to connect with an <b>App Password</b><br/>
            <a className="underline mt-2 inline-block" href="https://myaccount.google.com/apppasswords" target="_blank" rel="noreferrer">Get a Google App Password →</a>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
            {accounts.map((a) => (
              <div key={a.id} className="border border-zinc-900 p-3 bg-white" data-testid={`account-${a.id}`}>
                <div className="flex items-start justify-between">
                  <div className="min-w-0">
                    <div className="font-bold text-sm truncate">{a.display_name}</div>
                    <div className="font-mono text-xs text-zinc-600 truncate">{a.email}</div>
                    <div className="label-mono mt-1">{a.imap_host} · {a.total_messages} msgs</div>
                    {a.last_synced_at && <div className="label-mono mt-1">SYNCED {a.last_synced_at.slice(0, 16).replace("T", " ")}</div>}
                  </div>
                  <button onClick={() => removeAcc(a.id)} className="text-red-600 p-1" data-testid={`remove-account-${a.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                </div>
                <button onClick={() => sync(a)} disabled={syncingId === a.id} className="mt-2 w-full brut-btn px-2 py-1.5 text-xs flex items-center justify-center gap-1 disabled:opacity-50" data-testid={`sync-account-${a.id}`}>
                  <RefreshCw className={`w-3 h-3 ${syncingId === a.id ? "animate-spin" : ""}`} /> {syncingId === a.id ? "SYNCING..." : "SYNC LAST 30 DAYS"}
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="brut-card p-4 grid grid-cols-1 md:grid-cols-6 gap-3">
        <div className="md:col-span-2">
          <div className="label-mono mb-1">SEARCH</div>
          <div className="relative">
            <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-zinc-500" />
            <input value={filters.q} onChange={(e) => setFilters({ ...filters, q: e.target.value })} className="brut-input pl-7 pr-2 py-1.5 text-sm font-mono w-full" placeholder="subject, body, sender, attachments…" data-testid="email-search-input" />
          </div>
        </div>
        <div>
          <div className="label-mono mb-1">SEARCH IN</div>
          <select value={filters.search_scope} onChange={(e) => setFilters({ ...filters, search_scope: e.target.value })} className="brut-input px-2 py-1.5 text-xs font-mono w-full" data-testid="email-scope-select">
            <option value="all">Everything</option>
            <option value="subject">Subject only</option>
            <option value="body">Body</option>
            <option value="attachments">Attachments</option>
            <option value="sender">From / To / Cc</option>
          </select>
        </div>
        <div>
          <div className="label-mono mb-1">ACCOUNT</div>
          <select value={filters.account_id} onChange={(e) => setFilters({ ...filters, account_id: e.target.value })} className="brut-input px-2 py-1.5 text-xs font-mono w-full">
            <option value="">All accounts</option>
            {accounts.map((a) => <option key={a.id} value={a.id}>{a.email}</option>)}
          </select>
        </div>
        <div>
          <div className="label-mono mb-1">DIRECTION</div>
          <select value={filters.direction} onChange={(e) => setFilters({ ...filters, direction: e.target.value })} className="brut-input px-2 py-1.5 text-xs font-mono w-full">
            <option value="">All</option>
            <option value="received">Received</option>
            <option value="sent">Sent</option>
          </select>
        </div>
        <div>
          <div className="label-mono mb-1">JOB #</div>
          <input value={filters.job_number} onChange={(e) => setFilters({ ...filters, job_number: e.target.value })} className="brut-input px-2 py-1.5 text-xs font-mono w-full" placeholder="JOB-1001" data-testid="email-job-filter" />
        </div>
        <div>
          <div className="label-mono mb-1">ATTACHMENTS</div>
          <select value={filters.has_attachments} onChange={(e) => setFilters({ ...filters, has_attachments: e.target.value })} className="brut-input px-2 py-1.5 text-xs font-mono w-full">
            <option value="">Any</option>
            <option value="true">Has attachments</option>
            <option value="false">No attachments</option>
          </select>
        </div>
        <div>
          <div className="label-mono mb-1">FROM DATE</div>
          <input type="date" value={filters.date_from} onChange={(e) => setFilters({ ...filters, date_from: e.target.value })} className="brut-input px-2 py-1.5 text-xs font-mono w-full" data-testid="email-date-from" />
        </div>
        <div>
          <div className="label-mono mb-1">TO DATE</div>
          <input type="date" value={filters.date_to} onChange={(e) => setFilters({ ...filters, date_to: e.target.value })} className="brut-input px-2 py-1.5 text-xs font-mono w-full" data-testid="email-date-to" />
        </div>
      </div>

      {/* Messages table */}
      <div className="brut-card overflow-x-auto">
        <table className="w-full text-sm border-collapse">
          <thead>
            <tr className="bg-zinc-900 text-white">
              <Th>Dir</Th><Th>Date</Th><Th>From / To</Th><Th>Subject</Th><Th>Job #</Th><Th>Attach</Th><Th>Account</Th>
            </tr>
          </thead>
          <tbody>
            {messages.length === 0 && (
              <tr><td colSpan={7} className="text-center py-10 text-zinc-400 font-mono text-xs">NO MESSAGES — ADD A GMAIL ACCOUNT AND CLICK SYNC, OR IMPORT CSV</td></tr>
            )}
            {messages.map((m) => (
              <tr key={m.id} onClick={() => setSelected(m)} className="border-b border-zinc-200 hover:bg-zinc-50 cursor-pointer" data-testid={`email-row-${m.id}`}>
                <Td>
                  {m.direction === "sent" ? (
                    <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 bg-blue-100 border border-zinc-900">SENT</span>
                  ) : (
                    <span className="font-mono text-[10px] font-bold px-1.5 py-0.5 bg-green-100 border border-zinc-900">RCV</span>
                  )}
                </Td>
                <Td><span className="font-mono text-xs">{(m.date || "").slice(0, 16).replace("T", " ")}</span></Td>
                <Td><span className="font-mono text-xs truncate max-w-[200px] block">{m.direction === "sent" ? m.to_addr : m.from_addr}</span></Td>
                <Td><span className="font-bold truncate max-w-[400px] block">{m.subject || "(no subject)"}</span></Td>
                <Td>
                  {m.job_numbers?.length > 0 && (
                    <div className="flex flex-wrap gap-1">
                      {m.job_numbers.slice(0, 2).map((j) => <span key={j} className="font-mono text-[10px] bg-yellow-200 border border-zinc-900 px-1">{j}</span>)}
                    </div>
                  )}
                </Td>
                <Td>{m.has_attachments && <Paperclip className="w-3.5 h-3.5" />}</Td>
                <Td><span className="font-mono text-[10px] text-zinc-500 truncate max-w-[160px] block">{m.account_email}</span></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {showAdd && <GmailAppPasswordModal onClose={() => setShowAdd(false)} onAdded={() => { loadAccounts(); loadStats(); }} />}
      {selected && <MessageModal msg={selected} onClose={() => setSelected(null)} />}
    </div>
  );
}

function MessageModal({ msg, onClose }) {
  return (
    <div className="fixed inset-0 bg-zinc-900/40 z-50 flex items-center justify-center p-2 sm:p-4" onClick={onClose}>
      <div className="brut-card max-w-3xl w-full p-6 max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-3">
          <div className="min-w-0 flex-1">
            <div className="label-mono">{msg.direction?.toUpperCase()} · {(msg.date || "").slice(0, 16).replace("T", " ")}</div>
            <h2 className="font-heading text-2xl font-black mt-1 break-words">{msg.subject || "(no subject)"}</h2>
          </div>
          <button onClick={onClose} className="brut-btn-secondary p-1.5 ml-2"><X className="w-4 h-4" /></button>
        </div>
        <div className="space-y-1 text-sm border border-zinc-900 p-3 bg-zinc-50">
          <div><span className="label-mono mr-2">FROM</span><span className="font-mono text-xs">{msg.from_addr}</span></div>
          <div><span className="label-mono mr-2">TO</span><span className="font-mono text-xs">{msg.to_addr}</span></div>
          {msg.cc_addr && <div><span className="label-mono mr-2">CC</span><span className="font-mono text-xs">{msg.cc_addr}</span></div>}
          <div><span className="label-mono mr-2">ACCOUNT</span><span className="font-mono text-xs">{msg.account_email}</span></div>
          {msg.job_numbers?.length > 0 && (
            <div className="flex flex-wrap gap-1 pt-1">
              {msg.job_numbers.map((j) => <span key={j} className="font-mono text-[10px] bg-yellow-200 border border-zinc-900 px-2 py-0.5">{j}</span>)}
            </div>
          )}
        </div>
        {msg.attachments?.length > 0 && (
          <div className="mt-3">
            <div className="label-mono mb-1">ATTACHMENTS</div>
            <div className="flex flex-wrap gap-1">
              {msg.attachments.map((a, i) => (
                <span key={`${a}-${i}`} className="font-mono text-xs px-2 py-1 bg-orange-100 border border-zinc-900 flex items-center gap-1">
                  <Paperclip className="w-3 h-3" /> {a}
                </span>
              ))}
            </div>
          </div>
        )}
        <div className="mt-4">
          <div className="label-mono mb-1">BODY</div>
          <pre className="text-xs font-mono whitespace-pre-wrap border border-zinc-900 p-3 bg-white max-h-96 overflow-y-auto">{msg.body_snippet}</pre>
        </div>
      </div>
    </div>
  );
}

const Stat = ({ label, value, icon: Icon }) => (
  <div className="brut-card p-3 flex items-center justify-between">
    <div>
      <div className="label-mono">{label}</div>
      <div className="font-heading text-2xl font-black mt-1">{value}</div>
    </div>
    {Icon && <Icon className="w-5 h-5 text-zinc-400" />}
  </div>
);

const Th = ({ children }) => <th className="text-left px-3 py-2 label-mono text-white border-b border-zinc-900 whitespace-nowrap">{children}</th>;
const Td = ({ children }) => <td className="px-3 py-2.5">{children}</td>;
